import {
  InternalRefetchQueryDescriptor,
  useApolloClient,
} from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { Box } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import {
  RfItemAttachmentsBox_ItemFragment,
  RfItemAttachmentsBoxDocument,
  RfItemAttachmentsBoxQueryVariables,
  useRfItemAttachmentsAddMutation,
  useRfItemAttachmentsBoxQuery,
  useRfItemAttachmentsGetUploadUrlMutation,
  useRfRemoveItemAttachmentMutation,
  useSetRfItemAttachmentClientVisibilityMutation,
} from "./RfQuoteItemAttachmentsBox.generated.js";
import { DocType, PermissionName } from "../../../../clients/graphqlTypes.js";
import { RestrictedByDocumentPermissionByTypeWithDebug } from "../../../auth/RestrictedByDocumentPermissionByType.js";
import { RfItemFileBox } from "./RfItemFileBox.js";
import { makeRefetchQuery } from "../../../../clients/apollo-client-refetch-queries.js";
import { useUserData } from "../../../auth/useUserData.js";

interface Props {
  projectId: string | null;
  docId: string;
  docType: DocType;
  viewerPermissions: PermissionName[];
  item: RfItemAttachmentsBox_ItemFragment;
  isEditable?: boolean;
  isInitiallyClosed?: boolean;
  onUpdateDataRefetchQueries?: InternalRefetchQueryDescriptor[];
}

export const RfQuoteItemAttachmentsBox = ({
  projectId,
  docId,
  docType,
  viewerPermissions,
  item,
  isEditable = true,
  isInitiallyClosed,
  onUpdateDataRefetchQueries,
}: Props) => {
  const { t } = useTranslate("SapS4Hana");
  const client = useApolloClient();
  const viewer = useUserData().currentUser!;

  const { data, loading, refetch } = useRfItemAttachmentsBoxQuery({
    client,
    variables: { projectId: projectId!, docId: docId, itemId: item.id },
    skip: !projectId,
  });

  const attachments =
    getDataOrNull(data?.rfGetItemAttachments)?.attachments ?? [];

  const [getUploadUrl] = useRfItemAttachmentsGetUploadUrlMutation({
    client,
  });
  const [addAttachments] = useRfItemAttachmentsAddMutation({ client });
  const [removeAttachment] = useRfRemoveItemAttachmentMutation({ client });
  const [setClientVisibility] = useSetRfItemAttachmentClientVisibilityMutation({
    client,
  });

  const setAttachmentClientVisibility = React.useCallback(
    async (attachmentId: string, clientVisibility: boolean) => {
      await setClientVisibility({
        variables: {
          input: {
            projectId,
            docId,
            itemId: item.id,
            attachmentId,
            clientVisibility,
          },
        },
        refetchQueries: [
          ...(onUpdateDataRefetchQueries ?? []),
          makeRefetchQuery<RfItemAttachmentsBoxQueryVariables>({
            document: RfItemAttachmentsBoxDocument,
            variables: {
              projectId: projectId!,
              docId: docId,
              itemId: item.id,
            },
          }),
        ],
        awaitRefetchQueries: true,
      });
    },
    [docId, item.id, projectId, setClientVisibility, onUpdateDataRefetchQueries]
  );

  const canChangeVisibility =
    item.authorOrganisationId === viewer.organisation.id;

  const handleAdd = React.useCallback(
    async (files: File[]) => {
      try {
        // Get upload URL
        const uploadPromises = files.map(async file => {
          const { data: urlData } = await getUploadUrl({
            variables: {
              input: {
                docId: docId,
                itemId: item.id,
                filename: file.name,
                contentType: file.type || "application/octet-stream",
              },
            },
          });

          if (!urlData?.rfGetItemAttachmentUploadUrl) {
            throw new Error(`Failed to get upload URL for ${file.name}`);
          }

          const { uploadUrl, persistedUri } =
            urlData.rfGetItemAttachmentUploadUrl;

          // Upload to S3
          const response = await fetch(uploadUrl, {
            method: "PUT",
            body: file,
            headers: {
              "Content-Type": file.type || "application/octet-stream",
              "Content-Disposition": `attachment; filename="${file.name}"`,
            },
          });

          if (!response.ok) {
            throw new Error(`Failed to upload ${file.name}`);
          }

          return {
            title: file.name,
            mimeType: file.type || "application/octet-stream",
            url: persistedUri,
            docType: "",
          };
        });

        const attachmentsData = await Promise.all(uploadPromises);

        await addAttachments({
          variables: {
            input: {
              projectId: projectId!,
              docId: docId,
              itemId: item.id,
              attachments: attachmentsData,
            },
          },
          refetchQueries: [
            ...(onUpdateDataRefetchQueries ?? []),
            makeRefetchQuery<RfItemAttachmentsBoxQueryVariables>({
              document: RfItemAttachmentsBoxDocument,
              variables: {
                projectId: projectId!,
                docId: docId,
                itemId: item.id,
              },
            }),
          ],
        });
      } catch (error) {
        console.error("Upload error:", error);
        throw error;
      }
    },
    [
      projectId,
      docId,
      item.id,
      getUploadUrl,
      addAttachments,
      onUpdateDataRefetchQueries,
    ]
  );

  const handleDelete = React.useCallback(
    async (attachmentId: string) => {
      try {
        await removeAttachment({
          variables: {
            input: {
              projectId: projectId!,
              docId: docId,
              itemId: item.id,
              attachmentId,
            },
          },
          refetchQueries: [
            ...(onUpdateDataRefetchQueries ?? []),
            makeRefetchQuery<RfItemAttachmentsBoxQueryVariables>({
              document: RfItemAttachmentsBoxDocument,
              variables: {
                projectId: projectId!,
                docId: docId,
                itemId: item.id,
              },
            }),
          ],
        });
      } catch (error) {
        console.error("Delete error:", error);
        throw error;
      }
    },
    [projectId, docId, item.id, removeAttachment, onUpdateDataRefetchQueries]
  );

  return (
    <Box
      sx={{
        "& .MuiListItem-root:has(.sap-doc-type-dropdown)": {
          pr: 2,
          "& .MuiListItemSecondaryAction-root": {
            position: "relative",
            transform: "none",
            right: "auto",
          },
        },
        "& .MuiListItem-root:has(.sap-doc-type-dropdown) .MuiListItemButton-root":
          {
            pr: 2,
          },
      }}
    >
      <RfItemFileBox
        projectId={projectId}
        docId={docId}
        itemId={item.id}
        title={t("SAP attachments", { ns: "SapS4Hana" })}
        attachments={attachments}
        isInitiallyClosed={isInitiallyClosed}
        showImagesInGallery={false}
        readOnly={!isEditable}
        loading={loading}
        handleAdd={handleAdd}
        handleDelete={handleDelete}
        onDocTypeChange={() => refetch()}
        setClientVisibility={
          canChangeVisibility ? setAttachmentClientVisibility : undefined
        }
        renderAddButton={button => (
          <RestrictedByDocumentPermissionByTypeWithDebug
            permissionByType={{
              QUOTE: "MANAGE_QUOTES",
            }}
            document={{ docType, viewerPermissions }}
          >
            {button}
          </RestrictedByDocumentPermissionByTypeWithDebug>
        )}
        renderDeleteButton={button => (
          <RestrictedByDocumentPermissionByTypeWithDebug
            permissionByType={{
              QUOTE: "MANAGE_QUOTES",
            }}
            document={{ docType, viewerPermissions }}
          >
            {button}
          </RestrictedByDocumentPermissionByTypeWithDebug>
        )}
        renderDocTypeDropdown={dropdown => (
          <RestrictedByDocumentPermissionByTypeWithDebug
            permissionByType={{
              QUOTE: "MANAGE_QUOTES",
            }}
            document={{ docType, viewerPermissions }}
          >
            {dropdown}
          </RestrictedByDocumentPermissionByTypeWithDebug>
        )}
      />
    </Box>
  );
};
